/**
 * @file locales.ts
 * @description Internationalization locale definitions, default country mappings, and RTL text direction.
 * 
 * CORE RESPONSIBILITIES:
 * 1. Defines supported languages, native labels, and text directionality (LTR / RTL).
 * 2. Resolves primary default locales and currencies based on the venue's configured country.
 * 3. Builds localized public URLs for digital menus and custom domains.
 */

import type { Language } from './i18n';

export type PublicLocale = string;
export type PublicLanguage = 'en' | 'fr' | 'ar' | 'es' | 'de' | 'it' | 'pt' | 'ru' | 'zh' | 'ja' | 'nl' | 'tr' | 'ko' | 'el' | 'hi' | 'th' | 'id' | 'pl' | 'sv';

export const SUPPORTED_LANGUAGES: Array<{
  code: PublicLanguage;
  label: string;
  nativeLabel: string;
  dir: 'ltr' | 'rtl';
}> = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', dir: 'ltr' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', dir: 'ltr' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch', dir: 'ltr' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano', dir: 'ltr' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', dir: 'ltr' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский', dir: 'ltr' },
  { code: 'zh', label: 'Chinese', nativeLabel: '中文', dir: 'ltr' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語', dir: 'ltr' },
  { code: 'nl', label: 'Dutch', nativeLabel: 'Nederlands', dir: 'ltr' },
  { code: 'tr', label: 'Turkish', nativeLabel: 'Türkçe', dir: 'ltr' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어', dir: 'ltr' },
  { code: 'el', label: 'Greek', nativeLabel: 'Ελληνικά', dir: 'ltr' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', dir: 'ltr' },
  { code: 'th', label: 'Thai', nativeLabel: 'ไทย', dir: 'ltr' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia', dir: 'ltr' },
  { code: 'pl', label: 'Polish', nativeLabel: 'Polski', dir: 'ltr' },
  { code: 'sv', label: 'Swedish', nativeLabel: 'Svenska', dir: 'ltr' },
];

export const DEFAULT_PUBLIC_LOCALE: PublicLocale = 'en-ma';

export const PUBLIC_LOCALES: Array<{
  code: PublicLocale;
  hreflang: string;
  language: string;
  label: string;
  nativeLabel: string;
  dir: 'ltr' | 'rtl';
}> = SUPPORTED_LANGUAGES.map(lang => ({
  code: `${lang.code}-ma` as PublicLocale,
  hreflang: `${lang.code.toUpperCase()}-MA`,
  language: lang.code,
  label: lang.label,
  nativeLabel: lang.nativeLabel,
  dir: lang.dir,
}));

export const COUNTRY_OPTIONS = [
  // MENA
  { value: 'Morocco', label: 'Morocco 🇲🇦', description: 'North Africa' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates 🇦🇪', description: 'Middle East' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia 🇸🇦', description: 'Middle East' },
  { value: 'Egypt', label: 'Egypt 🇪🇬', description: 'North Africa' },
  { value: 'Qatar', label: 'Qatar 🇶🇦', description: 'Middle East' },
  { value: 'Kuwait', label: 'Kuwait 🇰🇼', description: 'Middle East' },
  { value: 'Bahrain', label: 'Bahrain 🇧🇭', description: 'Middle East' },
  { value: 'Oman', label: 'Oman 🇴🇲', description: 'Middle East' },
  { value: 'Jordan', label: 'Jordan 🇯🇴', description: 'Middle East' },
  { value: 'Algeria', label: 'Algeria 🇩🇿', description: 'North Africa' },
  { value: 'Tunisia', label: 'Tunisia 🇹🇳', description: 'North Africa' },
  // Latin America
  { value: 'Brazil', label: 'Brazil 🇧🇷', description: 'South America' },
  { value: 'Mexico', label: 'Mexico 🇲🇽', description: 'North America' },
  { value: 'Argentina', label: 'Argentina 🇦🇷', description: 'South America' },
  { value: 'Colombia', label: 'Colombia 🇨🇴', description: 'South America' },
  { value: 'Chile', label: 'Chile 🇨🇱', description: 'South America' },
  { value: 'Peru', label: 'Peru 🇵🇪', description: 'South America' },
  { value: 'Uruguay', label: 'Uruguay 🇺🇾', description: 'South America' },
  // Others
  { value: 'United States', label: 'United States 🇺🇸', description: 'North America' },
  { value: 'United Kingdom', label: 'United Kingdom 🇬🇧', description: 'Europe' },
  { value: 'France', label: 'France 🇫🇷', description: 'Europe' },
  { value: 'Spain', label: 'Spain 🇪🇸', description: 'Europe' },
  { value: 'Canada', label: 'Canada 🇨🇦', description: 'North America' },
];

export const CURRENCY_OPTIONS = [
  // MENA Currencies
  { value: 'MAD', label: 'Moroccan Dirham (MAD)', description: 'MAD' },
  { value: 'AED', label: 'UAE Dirham (AED)', description: 'AED' },
  { value: 'SAR', label: 'Saudi Riyal (SAR)', description: 'SAR' },
  { value: 'EGP', label: 'Egyptian Pound (EGP)', description: 'EGP' },
  { value: 'QAR', label: 'Qatari Riyal (QAR)', description: 'QAR' },
  { value: 'KWD', label: 'Kuwaiti Dinar (KWD)', description: 'KWD' },
  { value: 'BHD', label: 'Bahraini Dinar (BHD)', description: 'BHD' },
  { value: 'OMR', label: 'Omani Rial (OMR)', description: 'OMR' },
  { value: 'JOD', label: 'Jordanian Dinar (JOD)', description: 'JOD' },
  { value: 'DZD', label: 'Algerian Dinar (DZD)', description: 'DZD' },
  { value: 'TND', label: 'Tunisian Dinar (TND)', description: 'TND' },
  // Latin America Currencies
  { value: 'BRL', label: 'Brazilian Real (R$)', description: 'BRL' },
  { value: 'MXN', label: 'Mexican Peso ($)', description: 'MXN' },
  { value: 'ARS', label: 'Argentine Peso ($)', description: 'ARS' },
  { value: 'COP', label: 'Colombian Peso ($)', description: 'COP' },
  { value: 'CLP', label: 'Chilean Peso ($)', description: 'CLP' },
  { value: 'PEN', label: 'Peruvian Sol (S/)', description: 'PEN' },
  { value: 'UYU', label: 'Uruguayan Peso ($)', description: 'UYU' },
  // Others
  { value: 'USD', label: 'US Dollar ($)', description: 'USD' },
  { value: 'EUR', label: 'Euro (€)', description: 'EUR' },
  { value: 'GBP', label: 'British Pound (£)', description: 'GBP' },
  { value: 'CAD', label: 'Canadian Dollar ($)', description: 'CAD' },
];

export const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  Morocco: 'MAD',
  'United Arab Emirates': 'AED',
  'Saudi Arabia': 'SAR',
  Egypt: 'EGP',
  Qatar: 'QAR',
  Kuwait: 'KWD',
  Bahrain: 'BHD',
  Oman: 'OMR',
  Jordan: 'JOD',
  Algeria: 'DZD',
  Tunisia: 'TND',
  Brazil: 'BRL',
  Mexico: 'MXN',
  Argentina: 'ARS',
  Colombia: 'COP',
  Chile: 'CLP',
  Peru: 'PEN',
  Uruguay: 'UYU',
  'United States': 'USD',
  'United Kingdom': 'GBP',
  France: 'EUR',
  Spain: 'EUR',
  Canada: 'CAD',
};

export const getCurrencySymbol = (currency?: string | null): string => {
  if (!currency) return '$';
  const c = currency.toUpperCase();
  if (c === 'MAD') return 'DH';
  if (c === 'USD') return '$';
  if (c === 'EUR') return '€';
  if (c === 'GBP') return '£';
  if (c === 'AED') return 'AED';
  if (c === 'SAR') return 'SR';
  if (c === 'CAD') return 'C$';
  if (c === 'BRL') return 'R$';
  if (c === 'PEN') return 'S/';
  if (c === 'EGP') return 'E£';
  if (c === 'QAR') return 'QR';
  if (c === 'KWD') return 'KD';
  if (c === 'BHD') return 'BD';
  if (c === 'OMR') return 'OMR';
  if (c === 'JOD') return 'JD';
  if (c === 'DZD') return 'DA';
  if (c === 'TND') return 'DT';
  if (c === 'MXN' || c === 'ARS' || c === 'COP' || c === 'CLP' || c === 'UYU') return '$';
  return currency;
};

export const formatPriceWithCurrency = (price: number | string | null | undefined, currency?: string | null): string => {
  const numericPrice = typeof price === 'number' ? price : Number(price) || 0;
  const symbol = getCurrencySymbol(currency);
  const formattedPrice = numericPrice.toFixed(2);
  const c = (currency || 'MAD').toUpperCase();
  if (['MAD', 'AED', 'SAR', 'EGP', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'DZD', 'TND'].includes(c)) {
    return `${formattedPrice} ${symbol}`;
  }
  return `${symbol}${formattedPrice}`;
};

export const getItemPriceAndCurrency = (
  item: any,
  locale: PublicLocale,
  selectedCurrency?: string | null,
  venueCurrency?: string | null
): { price: number; original_price?: number | null; currency: string } => {
  const baseCurrency = venueCurrency || 'MAD';
  const targetCurrency = selectedCurrency || baseCurrency;

  // 1. Check if the active locale translation defines a custom price/currency
  if (item.translations) {
    // Check direct matching key (e.g. 'fr-ma')
    const directTrans = item.translations[locale];
    if (directTrans && directTrans.price !== undefined && directTrans.price !== null) {
      const transPrice = Number(directTrans.price);
      const transCurrency = directTrans.currency || targetCurrency;
      if (transCurrency.toUpperCase() === targetCurrency.toUpperCase()) {
        return {
          price: transPrice,
          original_price: directTrans.original_price ? Number(directTrans.original_price) : null,
          currency: transCurrency,
        };
      }
    }
    // Check generic language key (e.g. 'fr')
    const langKey = locale.split('-')[0];
    const genericTrans = item.translations[langKey];
    if (genericTrans && genericTrans.price !== undefined && genericTrans.price !== null) {
      const transPrice = Number(genericTrans.price);
      const transCurrency = genericTrans.currency || targetCurrency;
      if (transCurrency.toUpperCase() === targetCurrency.toUpperCase()) {
        return {
          price: transPrice,
          original_price: genericTrans.original_price ? Number(genericTrans.original_price) : null,
          currency: transCurrency,
        };
      }
    }
  }

  // 2. Check if any other translation has the target currency
  if (item.translations) {
    for (const trans of Object.values(item.translations)) {
      const t = trans as any;
      if (t && t.price !== undefined && t.price !== null && t.currency?.toUpperCase() === targetCurrency.toUpperCase()) {
        return {
          price: Number(t.price),
          original_price: t.original_price ? Number(t.original_price) : null,
          currency: t.currency,
        };
      }
    }
  }

  // 3. Fallback: use item's base price and convert to target currency if different
  const basePrice = item.price;
  const baseOriginalPrice = item.original_price;

  if (baseCurrency.toUpperCase() === targetCurrency.toUpperCase()) {
    return {
      price: basePrice,
      original_price: baseOriginalPrice,
      currency: baseCurrency,
    };
  }

  // Convert using basic exchange rates
  const EXCHANGE_RATES: Record<string, number> = {
    MAD: 1.0,
    USD: 0.10,
    EUR: 0.091,
    GBP: 0.078,
    AED: 0.37,
    SAR: 0.38,
    CAD: 0.14,
    EGP: 3.10,
    QAR: 0.36,
    KWD: 0.031,
    BHD: 0.038,
    OMR: 0.038,
    JOD: 0.071,
    DZD: 13.5,
    TND: 0.31,
    BRL: 0.54,
    MXN: 1.70,
    ARS: 90.0,
    COP: 400.0,
    CLP: 90.0,
    PEN: 0.37,
    UYU: 4.0,
  };

  const fromRate = EXCHANGE_RATES[baseCurrency.toUpperCase()] || 1.0;
  const toRate = EXCHANGE_RATES[targetCurrency.toUpperCase()] || 1.0;

  const rate = toRate / fromRate;
  const convertedPrice = basePrice * rate;
  const convertedOriginalPrice = baseOriginalPrice ? baseOriginalPrice * rate : null;

  return {
    price: convertedPrice,
    original_price: convertedOriginalPrice,
    currency: targetCurrency,
  };
};

export const isPublicLocale = (value: string | null | undefined): value is PublicLocale => {
  if (!value) return false;
  const parts = value.toLowerCase().split('-');
  if (parts.length !== 2) return false;
  const [lang, country] = parts;
  return SUPPORTED_LANGUAGES.some(l => l.code === lang) && country.length === 2;
};

export const normalizePublicLocale = (value: string | null | undefined): PublicLocale => {
  if (!value) return DEFAULT_PUBLIC_LOCALE;
  const normalized = value.toLowerCase() === 'en-uk' ? 'en-gb' : value.toLowerCase();
  return isPublicLocale(normalized) ? normalized : DEFAULT_PUBLIC_LOCALE;
};

export const getLocaleConfig = (locale: string | null | undefined) => {
  const norm = normalizePublicLocale(locale);
  const [lang, country] = norm.split('-');
  const langConfig = SUPPORTED_LANGUAGES.find(item => item.code === lang) || SUPPORTED_LANGUAGES[0];

  return {
    code: norm,
    hreflang: `${lang}-${(country || 'ma').toUpperCase()}`,
    language: langConfig.code,
    label: langConfig.label,
    nativeLabel: langConfig.nativeLabel,
    dir: langConfig.dir,
  };
};

export const getLocaleLanguage = (locale: string | null | undefined): Language =>
  getLocaleConfig(locale).language as Language;

export const getEnabledLocales = (
  enabledLocales: string[] | null | undefined,
  countryName?: string | null
): PublicLocale[] => {
  const locales = (enabledLocales || [])
    .map(normalizePublicLocale)
    .filter((locale, index, all) => all.indexOf(locale) === index);

  return locales.length > 0 ? locales : getCountryDefaultLocales(countryName).enabledLocales;
};

export const COUNTRY_CODES: Record<string, string> = {
  Morocco: 'ma',
  'United Arab Emirates': 'ae',
  'Saudi Arabia': 'sa',
  Egypt: 'eg',
  Qatar: 'qa',
  Kuwait: 'kw',
  Bahrain: 'bh',
  Oman: 'om',
  Jordan: 'jo',
  Algeria: 'dz',
  Tunisia: 'tn',
  Brazil: 'br',
  Mexico: 'mx',
  Argentina: 'ar',
  Colombia: 'co',
  Chile: 'cl',
  Peru: 'pe',
  Uruguay: 'uy',
  'United States': 'us',
  'United Kingdom': 'gb',
  France: 'fr',
  Spain: 'es',
  Canada: 'ca',
};

export const getCountryCode = (countryName: string | null | undefined): string => {
  if (!countryName) return 'ma';
  return COUNTRY_CODES[countryName] || 'ma';
};

export const getLocaleFlag = (locale: string | null | undefined): string => {
  if (!locale) return '🏳️';
  const norm = normalizePublicLocale(locale);
  const [lang, country] = norm.toLowerCase().split('-');
  
  if (lang === 'ar') {
    if (country === 'ma') return '🇲🇦';
    if (country === 'ae') return '🇦🇪';
    if (country === 'sa') return '🇸🇦';
    if (country === 'eg') return '🇪🇬';
    if (country === 'qa') return '🇶🇦';
    if (country === 'kw') return '🇰🇼';
    if (country === 'bh') return '🇧🇭';
    if (country === 'om') return '🇴🇲';
    if (country === 'jo') return '🇯🇴';
    if (country === 'dz') return '🇩🇿';
    if (country === 'tn') return '🇹🇳';
    return '🇦🇪';
  }
  
  const languageFlags: Record<string, string> = {
    en: '🇬🇧',
    fr: '🇫🇷',
    es: '🇪🇸',
    de: '🇩🇪',
    it: '🇮🇹',
    pt: '🇵🇹',
    ru: '🇷🇺',
    zh: '🇨🇳',
    ja: '🇯🇵',
    nl: '🇳🇱',
    tr: '🇹🇷',
  };
  
  return languageFlags[lang] || '🏳️';
};

export const getVenueLocalesList = (countryName: string | null | undefined) => {
  const cCode = getCountryCode(countryName);
  return SUPPORTED_LANGUAGES.map(lang => ({
    code: `${lang.code}-${cCode}`,
    hreflang: `${lang.code}-${cCode.toUpperCase()}`,
    language: lang.code as PublicLanguage,
    label: lang.label,
    nativeLabel: lang.nativeLabel,
    dir: lang.dir,
  }));
};

export const COUNTRY_DEFAULT_LOCALES: Record<string, { defaultLocale: PublicLocale; enabledLocales: PublicLocale[] }> = {
  Morocco: { defaultLocale: 'en-ma', enabledLocales: ['en-ma'] },
  'United Arab Emirates': { defaultLocale: 'ar-ae', enabledLocales: ['ar-ae'] },
  'Saudi Arabia': { defaultLocale: 'ar-sa', enabledLocales: ['ar-sa'] },
  Egypt: { defaultLocale: 'ar-eg', enabledLocales: ['ar-eg'] },
  Qatar: { defaultLocale: 'ar-qa', enabledLocales: ['ar-qa'] },
  Kuwait: { defaultLocale: 'ar-kw', enabledLocales: ['ar-kw'] },
  Bahrain: { defaultLocale: 'ar-bh', enabledLocales: ['ar-bh'] },
  Oman: { defaultLocale: 'ar-om', enabledLocales: ['ar-om'] },
  Jordan: { defaultLocale: 'ar-jo', enabledLocales: ['ar-jo'] },
  Algeria: { defaultLocale: 'fr-dz', enabledLocales: ['fr-dz'] },
  Tunisia: { defaultLocale: 'fr-tn', enabledLocales: ['fr-tn'] },
  Brazil: { defaultLocale: 'pt-br', enabledLocales: ['pt-br'] },
  Mexico: { defaultLocale: 'es-mx', enabledLocales: ['es-mx'] },
  Argentina: { defaultLocale: 'es-ar', enabledLocales: ['es-ar'] },
  Colombia: { defaultLocale: 'es-co', enabledLocales: ['es-co'] },
  Chile: { defaultLocale: 'es-cl', enabledLocales: ['es-cl'] },
  Peru: { defaultLocale: 'es-pe', enabledLocales: ['es-pe'] },
  Uruguay: { defaultLocale: 'es-uy', enabledLocales: ['es-uy'] },
  'United States': { defaultLocale: 'en-us', enabledLocales: ['en-us'] },
  'United Kingdom': { defaultLocale: 'en-gb', enabledLocales: ['en-gb'] },
  France: { defaultLocale: 'fr-fr', enabledLocales: ['fr-fr'] },
  Spain: { defaultLocale: 'es-es', enabledLocales: ['es-es'] },
  Canada: { defaultLocale: 'en-ca', enabledLocales: ['en-ca'] },
};

export const getCountryDefaultLocales = (
  countryName?: string | null
): { defaultLocale: PublicLocale; enabledLocales: PublicLocale[] } => {
  if (countryName && COUNTRY_DEFAULT_LOCALES[countryName]) {
    return COUNTRY_DEFAULT_LOCALES[countryName];
  }
  const cCode = getCountryCode(countryName);
  const def = `en-${cCode}` as PublicLocale;
  return {
    defaultLocale: def,
    enabledLocales: [def],
  };
};

export const resolveDefaultLocale = (
  defaultLocale: string | null | undefined,
  enabledLocales: string[] | null | undefined,
  countryName?: string | null
): PublicLocale => {
  const enabled = getEnabledLocales(enabledLocales, countryName);
  const normalized = normalizePublicLocale(defaultLocale);
  return enabled.includes(normalized) ? normalized : enabled[0];
};

export const buildMenuPath = (
  venueSlug: string,
  locale: string | null | undefined,
  departmentSlug?: string | null,
  tableId?: string | null
) => {
  const parts = ['', venueSlug, normalizePublicLocale(locale)];
  if (departmentSlug) parts.push(departmentSlug);
  const path = parts.join('/');
  return tableId ? `${path}?table=${encodeURIComponent(tableId)}` : path;
};

export const buildCustomDomainMenuPath = (
  locale: string | null | undefined,
  departmentSlug?: string | null,
  tableId?: string | null
) => {
  const parts = ['', normalizePublicLocale(locale)];
  if (departmentSlug) parts.push(departmentSlug);
  const path = parts.join('/');
  return tableId ? `${path}?table=${encodeURIComponent(tableId)}` : path;
};

export const CITIES_BY_COUNTRY: Record<string, string[]> = {
  Morocco: [
    'Casablanca', 'Rabat', 'Marrakech', 'Fes', 'Tangier', 'Agadir', 'Meknes',
    'Oujda', 'Kenitra', 'Tetouan', 'Essaouira', 'El Jadida', 'Safi', 'Mohammedia',
    'Nador', 'Beni Mellal', 'Dakhla', 'Laayoune'
  ],
  'United Arab Emirates': [
    'Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah',
    'Umm Al Quwain', 'Al Ain'
  ],
  'Saudi Arabia': [
    'Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Dhahran',
    'Tabuk', 'Abha', 'Taif', 'Jubail', 'Yanbu', 'Buraidah', 'Hail', 'Najran'
  ],
  Egypt: [
    'Cairo', 'Alexandria', 'Giza', 'Sharm El Sheikh', 'Hurghada', 'Luxor',
    'Aswan', 'Mansoura', 'Tanta', 'Port Said', 'Ismailia'
  ],
  Qatar: ['Doha', 'Al Rayyan', 'Al Wakrah', 'Al Khor', 'Umm Salal', 'Lusail'],
  Kuwait: ['Kuwait City', 'Hawalli', 'Salmiya', 'Al Ahmadi', 'Farwaniya', 'Jahra'],
  Bahrain: ['Manama', 'Riffa', 'Muharraq', 'Hamad Town', "A'ali", 'Isa Town'],
  Oman: ['Muscat', 'Salalah', 'Sohar', 'Nizwa', 'Sur', 'Seeb', 'Bawshar'],
  Jordan: ['Amman', 'Zarqa', 'Irbid', 'Aqaba', 'Salt', 'Madaba', 'Jerash'],
  Algeria: ['Algiers', 'Oran', 'Constantine', 'Annaba', 'Blida', 'Batna', 'Setif'],
  Tunisia: ['Tunis', 'Sfax', 'Sousse', 'Hammamet', 'Bizerte', 'Kairouan', 'Monastir'],
  'United Kingdom': [
    'London', 'Manchester', 'Birmingham', 'Edinburgh', 'Glasgow', 'Liverpool',
    'Bristol', 'Leeds', 'Newcastle', 'Sheffield', 'Oxford', 'Cambridge', 'Belfast', 'Cardiff'
  ],
  'United States': [
    'New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami', 'San Francisco',
    'Las Vegas', 'Dallas', 'Austin', 'Seattle', 'Boston', 'Atlanta', 'Denver', 'Washington D.C.'
  ],
  France: [
    'Paris', 'Lyon', 'Marseille', 'Nice', 'Bordeaux', 'Toulouse', 'Strasbourg',
    'Nantes', 'Lille', 'Montpellier', 'Cannes'
  ],
  Spain: [
    'Madrid', 'Barcelona', 'Valencia', 'Seville', 'Malaga', 'Bilbao', 'Zaragoza',
    'Palma de Mallorca', 'Granada', 'Alicante', 'San Sebastian'
  ],
  Canada: [
    'Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Ottawa', 'Edmonton', 'Quebec City', 'Winnipeg'
  ],
  Brazil: [
    'São Paulo', 'Rio de Janeiro', 'Brasília', 'Salvador', 'Fortaleza', 'Belo Horizonte', 'Curitiba'
  ],
  Mexico: [
    'Mexico City', 'Guadalajara', 'Monterrey', 'Cancun', 'Puebla', 'Tijuana', 'Playa del Carmen'
  ],
  Argentina: [
    'Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Mar del Plata', 'La Plata'
  ],
  Colombia: [
    'Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga'
  ],
  Chile: ['Santiago', 'Valparaíso', 'Concepción', 'Viña del Mar', 'Antofagasta'],
  Peru: ['Lima', 'Arequipa', 'Cusco', 'Trujillo', 'Chiclayo'],
  Uruguay: ['Montevideo', 'Punta del Este', 'Ciudad de la Costa', 'Salto']
};

export function getCountryCities(country?: string | null): string[] {
  if (!country) return CITIES_BY_COUNTRY['Morocco'];
  return CITIES_BY_COUNTRY[country] || ['Main City'];
}
